import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";

import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import Text from "@components/Text";
import Dialog from "@components/Dialog";
import Button from "@components/Button";
import { Column, Row } from "@components/Flex";
import { useLang } from "@src/hooks/useLang";
import TrackExportMenu, {
  downloadTrackExport,
  trackExportExt,
  type TTrackExportFormat,
} from "@screens/TracksScreen/TrackExportMenu";

type TFormat = TTrackExportFormat;

/**
 * "Export points" for the field tracks view: the season's raw points for this
 * field, every machine, as CSV, KML or Shapefile — the same dropdown as the
 * Tracks screen's export.
 *
 * When the preview is filtered (a color band, a speed range or a machine
 * hidden) the user picks whether the file should match the screen or carry
 * every point. Device settings — the admin speed band, potato sensor window —
 * apply either way; the backend enforces that.
 */
const FieldTracksExport: React.FC = () => {
  const lang = useLang();
  const f = lang.fields;
  const vm = useFieldsScreenVM();
  const [asking, setAsking] = useState<TFormat | null>(null);
  const [busy, setBusy] = useState(false);

  const download = async (format: TFormat, onlyFiltered: boolean) => {
    setAsking(null);
    const url = vm.trackPointsExportUrl(format, onlyFiltered);
    if (!url) return;
    const rawName = vm.selectedField?.get("name");
    const safeName = (typeof rawName === "string" && rawName ? rawName : "field").replaceAll(" ", "_");
    setBusy(true);
    try {
      await downloadTrackExport(url, `${safeName}_points.${trackExportExt(format)}`);
    } catch (e: any) {
      toast.error(e?.message ?? f.tracksExportFailed);
    } finally {
      setBusy(false);
    }
  };

  const start = (format: TFormat) => {
    if (vm.tracksFiltered) setAsking(format);
    else void download(format, false);
  };

  return (
    <>
      <TrackExportMenu
        variant="panel"
        label={f.tracksExportTitle}
        disabled={busy}
        onExport={(format) => start(format)}
      />
      {busy && (
        <>
          <div style={{ height: 4 }} />
          <Text size={11} color="#8c8f9b">
            {f.tracksExportPreparing}
          </Text>
        </>
      )}

      <Dialog
        visible={asking != null}
        onClose={() => setAsking(null)}
        title={f.tracksExportFilteredTitle}
        style={{ maxWidth: 420 }}
      >
        <Column crossAxisSize="max" style={{ gap: 16 }}>
          <Text size={14} color="#6B7280">
            {f.tracksExportFilteredBody}
          </Text>
          <Row justifyContent="flex-end" style={{ gap: 8, flexWrap: "wrap" }}>
            <Button kind="outline" onClick={() => setAsking(null)}>
              {f.tracksExportCancel}
            </Button>
            <Button kind="outline" onClick={() => asking && void download(asking, false)}>
              {f.tracksExportAll}
            </Button>
            <Button onClick={() => asking && void download(asking, true)}>
              {f.tracksExportFiltered}
            </Button>
          </Row>
        </Column>
      </Dialog>
    </>
  );
};

export default observer(FieldTracksExport);
